import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { apiRequest } from '../../api/client';

interface ScraperTarget {
  id: string;
  sourceType: string;
  targetType: string;
  value: string;
  displayName: string | null;
  priority: number;
  active: boolean;
  lastScraped: string | null;
  postCount: number;
  notes: string | null;
}

const AdminTargets: React.FC = () => {
  const { getToken } = useAuth();
  const [targets, setTargets] = useState<ScraperTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [formData, setFormData] = useState({
    sourceType: 'TWITTER',
    targetType: 'USERNAME',
    value: '',
    displayName: '',
    priority: 0,
    notes: '',
  });

  // Get keyword count
  const keywordCount = targets.filter(t => t.targetType === 'KEYWORD').length;

  const suggestedKeywords = [
    'hiring', 'job opening', 'we\'re looking for', 'join our team',
    'now hiring', 'open position', 'career opportunity', 'looking for',
    'seeking', 'job alert', 'remote position', 'full-time', 'part-time'
  ];

  const fetchTargets = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const params = new URLSearchParams({
        limit: '100',
        ...(sourceFilter && { sourceType: sourceFilter }),
      });

      const data = await apiRequest<{ targets: ScraperTarget[] }>(
        `/admin/targets?${params}`,
        {},
        token
      );
      setTargets(data.targets);

      // Auto-add default keywords if none exist
      const existingKeywords = data.targets.filter(t => t.targetType === 'KEYWORD');
      if (existingKeywords.length === 0) {
        await seedDefaultKeywords(token);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultKeywords = async (token: string | null) => {
    try {
      for (const keyword of suggestedKeywords) {
        await apiRequest('/admin/targets', {
          method: 'POST',
          body: JSON.stringify({
            sourceType: 'OTHER',
            targetType: 'KEYWORD',
            value: keyword.toLowerCase(),
            displayName: keyword,
            priority: 10,
            notes: 'Default keyword',
          }),
        }, token);
      }
      // Refresh to show the new keywords
      const params = new URLSearchParams({ limit: '100' });
      const data = await apiRequest<{ targets: ScraperTarget[] }>(
        `/admin/targets?${params}`,
        {},
        token
      );
      setTargets(data.targets);
    } catch (err) {
      console.error('Failed to seed default keywords:', err);
    }
  };

  useEffect(() => {
    fetchTargets();
  }, [getToken, sourceFilter]);

  const createTarget = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = await getToken();
      await apiRequest('/admin/targets', {
        method: 'POST',
        body: JSON.stringify(formData),
      }, token);
      setShowForm(false);
      setFormData({
        sourceType: 'TWITTER',
        targetType: 'USERNAME',
        value: '',
        displayName: '',
        priority: 0,
        notes: '',
      });
      fetchTargets();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const toggleActive = async (target: ScraperTarget) => {
    try {
      const token = await getToken();
      await apiRequest(`/admin/targets/${target.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !target.active }),
      }, token);
      fetchTargets();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const deleteTarget = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Delete this source?')) return;
    try {
      const token = await getToken();
      await apiRequest(`/admin/targets/${id}`, { method: 'DELETE' }, token);
      fetchTargets();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const addKeyword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keywordInput.trim()) return;

    try {
      const token = await getToken();
      await apiRequest('/admin/targets', {
        method: 'POST',
        body: JSON.stringify({
          sourceType: 'OTHER', // Keywords apply to all sources
          targetType: 'KEYWORD',
          value: keywordInput.trim().toLowerCase(),
          displayName: keywordInput.trim(),
          priority: 10,
          notes: 'Job keyword filter',
        }),
      }, token);
      setKeywordInput('');
      fetchTargets();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const sourceTypes = ['TWITTER', 'LINKEDIN', 'REDDIT', 'HACKERNEWS', 'DISCORD', 'TELEGRAM', 'OTHER'];
  const targetTypes = ['USERNAME', 'HASHTAG', 'SUBREDDIT', 'THREAD', 'KEYWORD', 'CHANNEL'];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-amber-400">Scraper Sources</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-amber-500 hover:bg-amber-600 text-oak-900 px-4 py-2 rounded-lg font-semibold transition"
        >
          {showForm ? 'Cancel' : 'Add Source'}
        </button>
      </div>

      {/* Keywords */}
      <div className="card-wood rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-cream-100">Keywords</h2>
          <span className="text-sm text-cream-200/50">{keywordCount} active</span>
        </div>
        <p className="text-sm text-cream-200/70 mb-3">
          Posts must contain at least one keyword to be scraped. Applies to all platforms.
        </p>
        <form onSubmit={addKeyword} className="flex gap-2 mb-3">
          <input
            type="text"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            placeholder="Add keyword..."
            className="flex-1 bg-oak-700 text-cream-100 px-3 py-2 rounded border border-oak-600"
          />
          <button
            type="submit"
            className="bg-amber-500 hover:bg-amber-600 text-oak-900 px-4 py-2 rounded font-semibold transition"
          >
            Add
          </button>
        </form>
        <div className="flex flex-wrap gap-2">
          {targets
            .filter(t => t.targetType === 'KEYWORD')
            .map((keyword) => (
              <span
                key={keyword.id}
                className="inline-flex items-center gap-1 bg-amber-500/20 text-amber-400 px-2 py-1 rounded text-sm"
              >
                {keyword.value}
                <button
                  onClick={() => deleteTarget(keyword.id)}
                  className="hover:text-red-400 ml-1"
                  title="Remove keyword"
                >
                  ×
                </button>
              </span>
            ))}
          {keywordCount === 0 && (
            <span className="text-cream-200/50 text-sm">No keywords configured</span>
          )}
        </div>
      </div>

      {/* Add Target Form */}
      {showForm && (
        <form onSubmit={createTarget} className="card-wood rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm text-cream-200/70 mb-1">Platform</label>
              <select
                value={formData.sourceType}
                onChange={(e) => setFormData({ ...formData, sourceType: e.target.value })}
                className="w-full bg-oak-700 text-cream-100 px-3 py-2 rounded border border-oak-600"
              >
                {sourceTypes.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-cream-200/70 mb-1">Type</label>
              <select
                value={formData.targetType}
                onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
                className="w-full bg-oak-700 text-cream-100 px-3 py-2 rounded border border-oak-600"
              >
                {targetTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-cream-200/70 mb-1">Value *</label>
              <input
                type="text"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="@username or #hashtag"
                required
                className="w-full bg-oak-700 text-cream-100 px-3 py-2 rounded border border-oak-600"
              />
            </div>
            <div>
              <label className="block text-sm text-cream-200/70 mb-1">Display Name</label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder="Friendly name"
                className="w-full bg-oak-700 text-cream-100 px-3 py-2 rounded border border-oak-600"
              />
            </div>
            <div>
              <label className="block text-sm text-cream-200/70 mb-1">Priority</label>
              <input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                className="w-full bg-oak-700 text-cream-100 px-3 py-2 rounded border border-oak-600"
              />
            </div>
            <div>
              <label className="block text-sm text-cream-200/70 mb-1">Notes</label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes"
                className="w-full bg-oak-700 text-cream-100 px-3 py-2 rounded border border-oak-600"
              />
            </div>
          </div>
          <button
            type="submit"
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition"
          >
            Create Source
          </button>
        </form>
      )}

      {/* Filter */}
      <div className="mb-6">
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="bg-oak-700 text-cream-100 px-3 py-2 rounded-lg border border-oak-600"
        >
          <option value="">All Platforms</option>
          {sourceTypes.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-400 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-cream-200/50">Loading...</div>
      ) : targets.filter(t => t.targetType !== 'KEYWORD').length === 0 ? (
        <div className="text-cream-200/50">No sources found</div>
      ) : (
        <div className="space-y-2">
          {targets.filter(t => t.targetType !== 'KEYWORD').map((target) => (
            <div
              key={target.id}
              className={`card-wood rounded-lg p-3 flex items-center justify-between ${
                !target.active ? 'opacity-50' : ''
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-oak-600 text-cream-200 px-2 py-0.5 rounded">
                      {target.sourceType}
                    </span>
                    <span className="text-xs bg-oak-700 text-cream-200/70 px-2 py-0.5 rounded">
                      {target.targetType}
                    </span>
                    <span className="font-semibold text-cream-100">{target.value}</span>
                    {target.displayName && (
                      <span className="text-cream-200/50 text-sm">({target.displayName})</span>
                    )}
                  </div>
                  <div className="text-xs text-cream-200/50 mt-1">
                    Priority: {target.priority} • Posts: {target.postCount}
                    {target.lastScraped && ` • Last: ${new Date(target.lastScraped).toLocaleDateString()}`}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleActive(target)}
                  className={`text-xs px-3 py-1 rounded ${
                    target.active
                      ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                  }`}
                >
                  {target.active ? 'Active' : 'Inactive'}
                </button>
                <button
                  onClick={() => deleteTarget(target.id)}
                  className="text-xs bg-red-500/20 text-red-400 px-3 py-1 rounded hover:bg-red-500/30"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTargets;
